import { Document, pdf } from "@react-pdf/renderer";
import EstimatePdf from "../../../../../planner/pdf/estimate-pdf";
import EstimateFFD from "../../../../../planner/pdf/estimate-pdf-ffd";
import EstimateMaterials from "../../../../../planner/pdf/estimate-materials";

import EstimateFFP from "../../../../../planner/pdf/estimate-pdf-ffp";
import TimelinesPDF from "../../../../../planner/pdf/timeline-pdf";
import TimelinesFFD from "../../../../../planner/pdf/timeline-pdf-ffd";
import { getProjectStartAndEndDate } from "../../../../../../utils/utils";

// planner‐type constants
const TYPE_TNM = "Time + Materials";
const TYPE_FFD = "Fixed Fee Deliverable";
const TYPE_FFP = "Fixed Fee Project";
const TYPE_M = "Materials";

export const exportEstimate = async ({
  projectData,
  companyDetails,
  contactName,
  forceFFP = false,
}) => {
  const { deliverables, materials, plannerType } = projectData;

  const isTnM = plannerType === TYPE_TNM;
  const isFFD = plannerType === TYPE_FFD;
  const isFFP = plannerType === TYPE_FFP;
  const isM = plannerType === TYPE_M;

  // MATERIALS VALIDATION: require one non-empty material.description
  if (isM) {
    const hasValidMaterials =
      Array.isArray(materials) &&
      materials.some((mat) => mat.description && mat.description.trim() !== "");
    if (!hasValidMaterials) {
      alert(
        "Unable to export estimate PDF. Please add at least one material description to proceed."
      );
      return;
    }
  }
  // Validation: every deliverable needs a name; for T&M also require a task + enabled resource
  if (!isM) {
    const hasValidDeliverables = deliverables?.some((d) => {
      if (!d.name?.trim()) return false;
      if (isTnM) {
        return d.tasks?.some(
          (t) => t.name?.trim() !== "" && t.resources?.some((r) => r.isEnabled)
        );
      }
      return true;
    });

    if (!hasValidDeliverables) {
      alert(
        "Unable to export estimate PDF. Please add a budget to your project plan to proceed."
      );
      return;
    }
  }
  // Pick the correct component
  let EstimateComponent = forceFFP
    ? EstimateFFD
    : isFFD
    ? EstimateFFD
    : isFFP
    ? EstimateFFP
    : isM
    ? EstimateMaterials
    : EstimatePdf;

  // Render + open
  const pdfData = (
    <EstimateComponent
      projectData={projectData}
      companyDetails={companyDetails}
      contactName={contactName}
      materials={materials}
      discountObj={{
        serviceDiscount: projectData?.serviceDiscount || 0,
        materialDiscount: projectData?.materialDiscount || 0,
        materialDiscountUnit: projectData?.materialDiscountUnit || "value",
        serviceDiscountUnit: projectData?.serviceDiscountUnit || "value",
      }}
    />
  );

  pdf(pdfData)
    .toBlob()
    .then((blob) => window.open(URL.createObjectURL(blob), "_blank"));
};

export const exportTimeline = ({
  projectData,
  companyDetails,
  contactName,
  forceSimple = false,
}) => {
  const { deliverables: milestones, materials, plannerType, id } = projectData;

  const isTnM = plannerType === TYPE_TNM;
  const isFFD = plannerType === TYPE_FFD;

  // Date‐completeness check
  const tasksMissingDates = milestones.some((m) =>
    m.tasks.some(
      (t) => t.description.trim() !== "" && (!t.startDate || !t.endDate)
    )
  );
  if (tasksMissingDates) {
    alert(
      "Unable to export. Please ensure every task has both a start and an end date before exporting the timeline."
    );
    return;
  }

  // Deliverable name check for all types, plus task‐description only for T&M
  const hasValidMilestones = milestones.some((m) => m.name.trim() !== "");
  const hasValidTasks = isTnM
    ? milestones.some((m) => m.tasks.some((t) => t.description.trim() !== ""))
    : true;

  if (!hasValidMilestones || !hasValidTasks) {
    alert(
      "Unable to display timeline PDF. Please ensure you have at least one deliverable name" +
        (isTnM ? " and one task description." : ".")
    );
    return;
  }

  // Pick the correct timeline component
  const TimelineComponent = forceSimple || isFFD ? TimelinesFFD : TimelinesPDF;
  const projectDateRange = getProjectStartAndEndDate(milestones);

  const pdfData = (
    <TimelineComponent
      id={id}
      companyDetails={companyDetails}
      contactName={contactName}
      materials={materials}
      milestones={milestones}
      projectData={projectData}
      projectDateRange={projectDateRange}
    />
  );

  pdf(pdfData)
    .toBlob()
    .then((blob) => window.open(URL.createObjectURL(blob), "_blank"));
};

export const exportGantt = ({ projectData, exportGanttChart }) => {
  const { milestones } = projectData;

  // Validate milestones and tasks
  const hasValidMilestones = milestones.some(
    (milestone) => milestone.name.trim() !== ""
  );
  const hasValidTasks = milestones.some((milestone) =>
    milestone.tasks.some(
      (task) => task.description.trim() !== "" && task.startDate && task.endDate
    )
  );

  if (!hasValidMilestones || !hasValidTasks) {
    alert("There is no Gantt data available.");
    return;
  }

  exportGanttChart();
};

const generateAndOpenPDF = async (pdfData) => {
  pdf(pdfData)
    .toBlob()
    .then((blob) => {
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, "_blank");
    });
};
